import type { Base } from 'active-resource';
import { Collection } from 'active-resource';
import type { WeekDay } from 'components/WeeklyTimeIntervals/WeeklyTimeIntervals.types';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import compact from 'lodash/compact';
import toString from 'lodash/toString';
import PlaceholderImage from 'public/images/defaults/candidate_image.png';
import { normalizeAbsoluteDate, shortMonthDayYear } from 'util/formatting/dates';
import type { Compensation } from 'util/job/compensation';
import type { CertificationResource } from 'util/resources/Certification';
import type { ConversationMemberResource } from 'util/resources/ConversationMember';
import type { EducationHistoryResource } from 'util/resources/EducationHistory';
import type { GeographyOptionResource } from 'util/resources/GeographyOption';
import type { InterviewResource } from 'util/resources/Interview';
import type { JobRecommendationResource } from 'util/resources/JobRecommendation';
import type { ProfileQuestionAnswerResource } from 'util/resources/ProfileQuestionAnswer';
import type { VideoAskAnswerResource } from 'util/resources/VideoAskAnswer';
import type { WorkHistoryResource } from 'util/resources/WorkHistory';
import type { GConstructor } from './APILibrary';
import APILibrary, { ClassExtender, GenerateResource } from './APILibrary';
import { AccountResource } from './Account';
import { GeographyResource } from './Geography';
import type { SystemNotificationResource } from './SystemNotification';
import { isJSONParsable } from 'util/validations';
import type { ProfileQuestionResource } from 'util/resources/ProfileQuestion';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import toNumber from 'lodash/toNumber';
import { EmployerSavedFilterSelectionResource } from 'util/resources/EmployerSavedFilterSelection';
import { TeacherSavedFilterSelectionResource } from 'util/resources/TeacherSavedFilterSelection';

type TimeInterval = {
  startTime: string;
  endTime: string;
};
type LastEmployerMessage = {
  createdAt?: string;
  employerProfileFirstName?: string;
  employerProfileId?: string;
  data?: string;
};

type LastUserProfileMessage = {
  createdAt?: string;
  readAt?: string;
  data?: string;
  profileType?: string;
  profileId?: string;
};

type ConversationData = {
  conversationInitialized?: boolean;
  employerLastMessage?: {
    senderName: string;
    date: string;
  };
  lastMessageInfo?: {
    date: string;
    source: string;
  };
  educatorHasMessaged?: boolean;
  hasUnreadMessages?: boolean;
};

const LOOKING_FOR_PROFILE_QUESTION_CATEGORIES = ['job_preferences'];
const LOOKING_FOR_PROFILE_QUESTION_NAMES = [
  'desired_role', // part-time, full time etc
  'desired_job', // assistant teacher, lead teacher etc
  'desired_location',
  'desired_start_month',
  'desired_age_group',
];

/**
 * Relationship classes
 */
export class UserProfileAttributes {
  aboutMeAnalysis: {
    mentionsChildren: boolean;
    relevantExperience: string[];
    meetsCriteria?: number[];
    rejectionExplanation?: string;
  };
  activeRecently: boolean = null;
  lastOnlineAt?: string;
  activeState: string = null;
  approvalState: string = null;
  avatar: {
    filename?: string;
    downloadUrl?: string;
    smallUrl?: string;
    mediumUrl?: string;
    largeUrl?: string;
  } = null;
  city: string = null;
  completedEceCredits: {
    units: number;
  } = null;
  createdAt: string = null;
  email: string = null;
  firstName: string = null;
  geographyId: string = null;
  id: string = null;
  liked: boolean = null;
  indeedResume: string = null;
  lastName: string = null;
  missingRequiredFields: string[] = null;
  missingScreeningQuestions: boolean = null;
  phoneNumber: string = null;
  previouslyApplied: {
    id: string;
    createdAt: string;
    locations: string[];
    title: string;
  }[] = null;
  recommended: boolean = null;
  responsive: boolean = null;
  resume: {
    contentType: string;
    downloadUrl: string;
    checksum: string;
    filename: string;
    emailListSizeUrl: string;
    smallUrl: string;
    mediumUrl: string;
    largeUrl: string;
  } = null;

  attachments: {
    filename: string;
    downloadUrl: string;
  }[] = null;
  showCertifications: boolean = null;
  showEducation: boolean = null;
  slug: string = null;
  state: string = null;
  visibilityState: string = null;
  zipCode: string = null;

  // TODO: metadata is not part of this resource
  metadata: {
    conversation?: {
      lastEmployerMessage?: LastEmployerMessage;
      lastUserProfileMessage?: LastUserProfileMessage;
    };
  } = null;
}

export class UserProfileBelongsTo {
  account: () => AccountResource = () => new AccountResource();
  geography: () => GeographyResource = () => new GeographyResource();
}

export class UserProfileHasOne {}

export class UserProfileHasMany {
  certifications: () => Collection<CertificationResource> = () =>
    new Collection<CertificationResource>();
  conversationMembers: () => Collection<ConversationMemberResource> = () =>
    new Collection<ConversationMemberResource>();
  educationHistories: () => Collection<EducationHistoryResource> = () =>
    new Collection<EducationHistoryResource>();
  jobRecommendations: () => Collection<JobRecommendationResource> = () =>
    new Collection<JobRecommendationResource>();
  profileQuestionAnswers: () => Collection<ProfileQuestionAnswerResource> = () =>
    new Collection<ProfileQuestionAnswerResource>();
  workHistories: () => Collection<WorkHistoryResource> = () =>
    new Collection<WorkHistoryResource>();
  videoAskAnswers: () => Collection<VideoAskAnswerResource> = () =>
    new Collection<VideoAskAnswerResource>();
  interviews: () => Collection<InterviewResource> = () => new Collection<InterviewResource>();
  systemNotifications: () => Collection<SystemNotificationResource> = () =>
    new Collection<SystemNotificationResource>();
  savedFilterSelections: () => Collection<TeacherSavedFilterSelectionResource> = () =>
    new Collection<TeacherSavedFilterSelectionResource>();
}

/**
 * Utility interfaces
 */
export interface UserProfileMethods {
  avatarSmall: () => string | typeof PlaceholderImage;
  isEmployer: () => boolean;
  linkToProfile: () => string;
  fullName: () => string;
  getLookingForAnswers: () => ProfileQuestionAnswerResource[];
  getLookingForAnswersAsText: () => string[];
  getStartDate: () => string;
  isActivelyLookingForJob: () => boolean;
  getJobSearchStatus: () => string;
  getLocationText: () => string;
  getConversationData: () => ConversationData;
  getMostRelevantEducation: () => EducationHistoryResource;
  getAnswerValues: (
    questionAnswer: ProfileQuestionAnswerResource,
    defaultValue: unknown,
  ) => unknown;
  getQuestion: (name: string) => ProfileQuestionAnswerResource;
  desiredRoles: () => string[];
  schedule: () => Record<Capitalize<WeekDay>, TimeInterval[]>;
  ageGroups: () => string[];
  expectedCompensation: () => Compensation;
  startDate: () => string;
  locationIds: () => string[];
  desiredJobs: () => string[];
  desiredLocations: (geographyOptions: {
    location: GeographyOptionResource[];
  }) => GeographyOptionResource[];
  getFirstUnansweredProfileQuestion: (
    profileQuestions: ProfileQuestionResource[],
    profileQuestionAnswers: ProfileQuestionAnswerResource[],
  ) => ProfileQuestionResource;
}

export interface UserProfileInterface
  extends UserProfileAttributes,
    UserProfileBelongsTo,
    UserProfileHasMany,
    UserProfileType {}

type UserProfileType = GConstructor<UserProfileInterface>;

export interface UserProfileResource
  extends Base<UserProfileAttributes>,
    UserProfileInterface,
    UserProfileMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<UserProfileAttributes>({
    queryName: 'user_profiles',
    className: 'UserProfile',
    AttributesClass: UserProfileAttributes,
    BelongsToClasses: [
      {
        Klass: UserProfileBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: UserProfileHasMany,
      },
    ],
  });

export const UserProfileResourceClass = <TBase extends UserProfileType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements UserProfileMethods {
    avatarSmall() {
      if (this.avatar && this.avatar.smallUrl) {
        return this.avatar?.smallUrl;
      }
      return PlaceholderImage;
    }

    isEmployer() {
      return false;
    }

    linkToProfile() {
      return '/educators/' + this.slug;
    }

    fullName() {
      return compact([this.firstName, this.lastName]).join(' ').trim();
    }

    getLookingForAnswers() {
      return this.profileQuestionAnswers()
        .toArray()
        .filter((answer) => {
          return (
            LOOKING_FOR_PROFILE_QUESTION_CATEGORIES.includes(answer.profileQuestion().category) &&
            LOOKING_FOR_PROFILE_QUESTION_NAMES.includes(answer.profileQuestion().name)
          );
        });
    }

    getStartDate() {
      const ans = this.profileQuestionAnswers()
        .toArray()
        .find((ans) => ans.profileQuestion().name === 'desired_start_date');

      if (!ans) {
        return null;
      }

      const date = new Date(toString(ans.value));

      if (isNaN(date.getDate())) {
        return null;
      }

      if (date < new Date(Date.now())) {
        return 'ASAP';
      }
      return toString(ans.value);
    }

    getLookingForAnswersAsText(): string[] {
      const QUESTIONS_WITH_STRING_ARRAY_ANSWERS = [
        'desired_start_month',
        'desired_role',
        'desired_job',
        'desired_age_group',
      ];

      return this.getLookingForAnswers().reduce((answers, answer) => {
        if (!answer?.value) {
          return answers;
        }

        switch (answer.profileQuestion().name) {
          case 'desired_start_month':
            if (isJSONParsable(toString(answer.value))) {
              return [
                ...answers,
                `Starting ${(JSON.parse(toString(answer.value)) as string[])
                  .map((startMonth) => (startMonth === 'ASAP' ? 'ASAP' : `in ${startMonth}`))
                  .join(', ')}`,
              ];
            }

            return [...answers];

          default:
            if (QUESTIONS_WITH_STRING_ARRAY_ANSWERS.includes(answer.profileQuestion().name)) {
              if (isJSONParsable(toString(answer.value))) {
                return [...answers, ...(JSON.parse(toString(answer.value)) as string[])];
              }

              return [...answers];
            }
        }
        return answers;
      }, [] as string[]);
    }

    isActivelyLookingForJob() {
      const jobSearchStatusAnswer = this.profileQuestionAnswers()
        .toArray()
        .find((answer) => answer.profileQuestion().name === 'job_search_status');

      return (
        toString(jobSearchStatusAnswer?.value).toLowerCase() ===
        'i am actively looking for a new role'
      );
    }

    getJobSearchStatus() {
      const jobSearchStatusAnswer = this.profileQuestionAnswers()
        .toArray()
        .find((answer) => answer.profileQuestion().name === 'job_search_status');

      return toString(jobSearchStatusAnswer?.value);
    }

    getLocationText = () => {
      const cityState = compact([this.city, this.state]).join(', ');

      if (!this.zipCode) {
        return cityState;
      }

      return `${cityState} ${this.zipCode}`;
    };

    getConversationData() {
      const conversation = this.metadata?.conversation;

      if (!conversation) {
        return {};
      }

      const { lastEmployerMessage, lastUserProfileMessage } = conversation;

      let lastMessage: LastUserProfileMessage | LastEmployerMessage;

      [lastUserProfileMessage, lastEmployerMessage].forEach((message) => {
        if (!message?.createdAt) {
          return;
        }
        if (!lastMessage) {
          lastMessage = message;
          return;
        }

        if (new Date(message.createdAt) > new Date(lastMessage.createdAt)) {
          lastMessage = message;
        }
      });

      return {
        conversationInitialized: true,
        employerLastMessage: lastEmployerMessage && {
          senderName: lastEmployerMessage.employerProfileFirstName,
          date: shortMonthDayYear(lastEmployerMessage.createdAt),
        },
        lastMessageInfo: lastMessage && {
          date: shortMonthDayYear(lastMessage.createdAt),
          source: lastMessage.hasOwnProperty('employerProfileId') ? 'Employer' : 'UserProfile',
        },
        educatorHasMessaged: !!lastUserProfileMessage,
        hasUnreadMessages: !!lastUserProfileMessage && !lastUserProfileMessage.readAt,
      };
    }

    getMostRelevantEducation() {
      const educationHistories = this.educationHistories().toArray();

      if (!educationHistories?.length) {
        return null;
      }

      if (educationHistories.length === 1) {
        return educationHistories[0];
      }

      const educationHistoriesInProgress = educationHistories.filter((edu) => !!edu.current);

      if (!!educationHistoriesInProgress?.length) {
        return educationHistoriesInProgress[0];
      }

      const completedEducationHistories = educationHistories.filter(
        (edu) => !edu.current && edu.endDate,
      );

      return completedEducationHistories.sort(
        (a, b) => new Date(b.endDate).valueOf() - new Date(a.endDate).valueOf(),
      )[0];
    }

    getAnswerValues = (questionAnswer: ProfileQuestionAnswerResource, defaultValue): unknown => {
      const rawValue = questionAnswer?.value;
      if (rawValue) {
        try {
          return JSON.parse(toString(rawValue));
        } catch {
          return rawValue;
        }
      }
      return defaultValue;
    };

    getQuestion = (name) => {
      const questionAnswers = this.profileQuestionAnswers().toArray();

      return questionAnswers.find((ans) => ans.profileQuestionName == name);
    };

    desiredRoles(): string[] {
      return this.getAnswerValues(this.getQuestion('desired_role'), []) as string[];
    }

    schedule(): Record<Capitalize<WeekDay>, TimeInterval[]> {
      return this.getAnswerValues(this.getQuestion('availability'), {}) as Record<
        Capitalize<WeekDay>,
        TimeInterval[]
      >;
    }

    ageGroups(): string[] {
      return this.getAnswerValues(this.getQuestion('desired_age_group'), []) as string[];
    }

    expectedCompensation() {
      const expectedCompensationAnswer = this.getAnswerValues(
        this.getQuestion('expected_compensation'),
        {},
      ) as Compensation;

      return expectedCompensationAnswer;
    }

    startDate(): string {
      const desiredStart = this.getAnswerValues(this.getQuestion('desired_start'), '') as string;

      if (!desiredStart) {
        const desiredStartMonth = this.getAnswerValues(
          this.getQuestion('desired_start_month'),
          [],
        ) as string[];

        if (desiredStartMonth.includes('ASAP')) {
          return 'ASAP';
        }

        return null;
      }

      switch (desiredStart.toLowerCase()) {
        case 'asap':
          return 'ASAP';
        case 'other':
          const desiredStartDate = this.getAnswerValues(
            this.getQuestion('desired_start_date'),
            '',
          ) as string;
          const desiredStartAsDate = new Date(desiredStartDate);

          if (!desiredStartDate || !isValid(desiredStartAsDate)) {
            return null;
          }

          return `After ${format(
            normalizeAbsoluteDate(new Date(desiredStartDate)),
            'MMMM dd, yyyy',
          )}`;
        case 'i have to give notice to my current employer':
          const noticePeriod = this.getAnswerValues(
            this.getQuestion('notice_period'),
            '',
          ) as string;

          if (!noticePeriod) {
            return null;
          }

          return `${noticePeriod} required`;
        default:
          return (
            this.getAnswerValues(this.getQuestion('desired_start_month'), []) as string[]
          ).join(', ');
      }
    }

    locationIds(): string[] {
      return this.getAnswerValues(this.getQuestion('desired_location'), []) as string[];
    }

    desiredJobs(): string[] {
      return this.getAnswerValues(this.getQuestion('desired_job'), []) as string[];
    }

    desiredLocations = (geographyOptions: {
      location: GeographyOptionResource[];
    }): GeographyOptionResource[] => {
      if (geographyOptions) {
        return this.locationIds().map((locId) => {
          return geographyOptions.location.find((geoOpt) => geoOpt.id === locId);
        });
      }

      return [];
    };

    getFirstUnansweredProfileQuestion = (
      profileQuestions: ProfileQuestionResource[],
      profileQuestionAnswers: ProfileQuestionAnswerResource[],
    ) => {
      if (!profileQuestions?.length || !profileQuestionAnswers) {
        return undefined;
      }

      const addedUnlistedProfileQuestions = compact(
        reduce(
          profileQuestionAnswers,
          (acc, pqa) => {
            const profileQuestionAnswerQuestion = pqa.profileQuestion();

            if (!profileQuestionAnswerQuestion) {
              return acc;
            }

            const selectedOption = find(
              profileQuestionAnswerQuestion.options,
              (option) => option.value === pqa.value,
            );

            if (!selectedOption) {
              return acc;
            }

            const addUnlistedProfileQuestionIds =
              selectedOption.flowControl?.addUnlistedProfileQuestionIds;

            if (isEmpty(addUnlistedProfileQuestionIds)) {
              return acc;
            }

            const addedUnlistedQuestions = reduce(
              addUnlistedProfileQuestionIds,
              (acc, id) => {
                const unlistedQuestion = find(profileQuestions, (pq) => pq.id === id);

                if (!unlistedQuestion) {
                  return acc;
                }

                return [...acc, unlistedQuestion];
              },
              [] as ProfileQuestionResource[],
            );

            return [...acc, ...flatten(addedUnlistedQuestions)];
          },
          [] as ProfileQuestionResource[],
        ),
      );

      const firstAddedUnlistedRejectionQuestion = find(
        addedUnlistedProfileQuestions,
        (profileQuestion) => includes(['rejection', 'final_rejection'], profileQuestion.inputType),
      );

      if (firstAddedUnlistedRejectionQuestion) {
        return firstAddedUnlistedRejectionQuestion;
      }

      const listedQuestions = reject(profileQuestions, 'unlisted');

      const listedAndUnlistedQuestions = sortBy(
        [...listedQuestions, ...addedUnlistedProfileQuestions],
        (profileQuestion) => toNumber(profileQuestion.order),
      );

      const firstUnansweredQuestion = listedAndUnlistedQuestions.find((profileQuestion) => {
        const answer = find(
          profileQuestionAnswers,
          (profileQuestionAnswer) => profileQuestionAnswer.profileQuestionId === profileQuestion.id,
        );

        return !answer;
      });

      if (firstUnansweredQuestion) {
        return firstUnansweredQuestion;
      }

      const requiredMissingQuestion = find(profileQuestions, (profileQuestion) =>
        this.missingRequiredFields.includes(profileQuestion.name),
      );

      if (requiredMissingQuestion) {
        return requiredMissingQuestion;
      }

      return undefined;
    };
  };

export class UserProfileResource extends ClassExtender<UserProfileInterface>(
  BaseClass,
  UserProfileResourceClass,
) {}

/**
 * Resource instance
 */
export const UserProfile = APILibrary.createResource<UserProfileResource>(UserProfileResource);
